import { createContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { AuthService, type UserProfile } from '../../lib/auth';
import { TokenManager } from '../../lib/tokenManager';
import { syncProfileFromToken, clearStaleSessionData } from '../../lib/profileSync';
import type { User } from '@supabase/supabase-js';

interface UserContextType {
  // Anonymous user state (existing)
  userId: string | null;
  sessionId: string | null;
  firstName: string | null;
  setUserId: (id: string | null) => void;
  setSessionId: (id: string | null) => void;
  setFirstName: (name: string | null) => void;
  clearUser: () => void;

  // Profile sync state (new - for preventing stale ID locks)
  isProfileSynced: boolean;
  isSyncing: boolean;

  // Authentication state
  authUser: User | null;
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  authLoading: boolean;

  // Authentication methods
  createAccount: (email: string, password: string, firstName?: string) => Promise<{ success: boolean; error?: string }>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<{ success: boolean; error?: string }>;
  refreshProfile: () => Promise<void>;

  // Profile sync method (for PreTestForm to call after profile creation)
  triggerProfileSync: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  // Anonymous user state (existing)
  const [userId, setUserId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('cubetest-userId');
    }
    return null;
  });

  const [sessionId, setSessionId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('cubetest-sessionId');
    }
    return null;
  });

  const [firstName, setFirstName] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('cubetest-firstName');
    }
    return null;
  });

  // Profile sync state (new - prevents stale ID locks)
  const [isProfileSynced, setIsProfileSynced] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncAttemptedRef = useRef(false); // Prevent duplicate sync attempts
  const lastSyncedTokenRef = useRef<string | null>(null); // Track which token we last synced

  // Authentication state
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const postSigninCalledRef = useRef(false); // Guard against duplicate post-signin calls

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Get session directly from Supabase to avoid restore loop
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user ?? null;
        setAuthUser(user);

        if (user) {
          const profile = await AuthService.getUserProfile(user.id);
          if (profile) {
            setUserProfile(profile);
            // Also set the anonymous user state for compatibility
            setUserIdPersistent(profile.id);
            setFirstNamePersistent(profile.first_name);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 UserContext: Auth state changed:', event, session?.user?.id);
        setAuthUser(session?.user ?? null);

        if (session?.user) {
          const authUserId = session.user.id;
          const email = session.user.email;

          console.log('👤 UserContext: User authenticated:', { authUserId, email });

          // CRITICAL: Store Supabase session tokens in TokenManager for REST API client
          if (session.access_token && session.refresh_token && session.expires_in) {
            console.log('💾 UserContext: Storing Supabase session in TokenManager...');
            TokenManager.storeTokens({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
              expires_in: session.expires_in,
              user: {
                id: authUserId,
                email: email || undefined
              }
            });
            console.log('✅ UserContext: Tokens stored in TokenManager successfully');

            // Call post-signin endpoint to link profiles (service-role operation)
            // Handle both SIGNED_IN (password login) and INITIAL_SESSION (magic link, page load)
            // Guard: only call if we haven't already (prevents duplicate calls on same mount)
            if (email && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && !postSigninCalledRef.current) {
              console.log('[UserContext] Calling post-signin endpoint to link profiles...');
              postSigninCalledRef.current = true; // Set guard before async call
              try {
                const response = await fetch('/api/auth/post-signin', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  },
                  body: JSON.stringify({
                    authUserId,
                    email,
                    firstName: session.user.user_metadata?.first_name || null
                  })
                });

                if (!response.ok) {
                  const errorText = await response.text();
                  console.error('[UserContext] Post-signin endpoint failed:', {
                    status: response.status,
                    error: errorText
                  });

                  // Set minimal profile to prevent infinite loading
                  // User is authenticated, just profile linking failed
                  setUserProfile({
                    id: authUserId, // Temporary - use auth user ID
                    auth_user_id: authUserId,
                    email,
                    first_name: session.user.user_metadata?.first_name || null,
                    is_authenticated: true,
                    age: null,
                    country: null,
                    gender: null,
                    occupation: null,
                    education_level: null,
                    relationship_status: null,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  });

                  // Still set localStorage for compatibility
                  setUserIdPersistent(authUserId);
                  setFirstNamePersistent(session.user.user_metadata?.first_name || null);

                } else {
                  const result = await response.json();
                  console.log('[UserContext] Post-signin complete:', {
                    profileId: result.profile?.id,
                    mergedProfiles: result.mergedProfiles
                  });

                  if (result.profile) {
                    setUserProfile(result.profile);
                    setUserIdPersistent(result.profile.id);
                    setFirstNamePersistent(result.profile.first_name);

                    // Store access_token for /api/create-session
                    if (result.profile.access_token) {
                      localStorage.setItem('user_access_token', result.profile.access_token);
                      console.log('💾 UserContext: Stored profile access_token for authenticated user');
                    } else {
                      console.warn('⚠️ UserContext: Profile missing access_token');
                    }
                  }
                }
              } catch (err) {
                console.error('[UserContext] Failed to call post-signin endpoint:', err);

                // Set minimal profile to prevent infinite loading
                setUserProfile({
                  id: authUserId,
                  auth_user_id: authUserId,
                  email,
                  first_name: session.user.user_metadata?.first_name || null,
                  is_authenticated: true,
                  age: null,
                  country: null,
                  gender: null,
                  occupation: null,
                  education_level: null,
                  relationship_status: null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                });

                setUserIdPersistent(authUserId);
                setFirstNamePersistent(session.user.user_metadata?.first_name || null);
              }
            }
          } else {
            console.warn('⚠️ UserContext: Session missing tokens, cannot store in TokenManager');
          }

          // Note: Profile linking is now handled by the post-signin endpoint above
          // This ensures atomic operations via service role and bypasses RLS issues
        } else {
          // No session - could be initial load (before Supabase restores session) OR explicit signout
          console.log('🔄 UserContext: No session -', event === 'SIGNED_OUT' ? 'user signed out' : 'session cleared');

          // CRITICAL: Only clear TokenManager on explicit SIGNED_OUT event
          // Don't clear on INITIAL_SESSION with null session (Supabase might still be loading persisted session)
          if (event === 'SIGNED_OUT') {
            setUserProfile(null);
            TokenManager.clearTokens();
            postSigninCalledRef.current = false; // Reset guard for next signin
            console.log('🗑️ UserContext: Cleared state - ready for next signin');
          } else {
            // Initial load or other event - don't clear TokenManager yet
            // Supabase will restore the session automatically if one exists
            console.log('ℹ️ UserContext: No session on initial load, waiting for Supabase to restore...');
          }
        }

        setAuthLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []); // Empty deps - subscribe once on mount, unsubscribe on unmount

  // Bootstrap profile sync for anonymous users
  // This runs once on mount if an anon access_token exists
  // Ensures userId in state/localStorage matches the token BEFORE /test page loads
  useEffect(() => {
    const performBootstrapSync = async () => {
      if (typeof window === 'undefined') return;

      const accessToken = localStorage.getItem('user_access_token');

      // No token = nothing to sync
      if (!accessToken) {
        console.log('🔄 UserContext: No access_token found, skipping bootstrap sync');
        setIsProfileSynced(true); // Mark as synced (nothing to sync)
        return;
      }

      // If we've already synced this token, skip
      if (lastSyncedTokenRef.current === accessToken) {
        console.log('🔄 UserContext: Already synced this token, skipping bootstrap sync');
        setIsProfileSynced(true);
        return;
      }

      // If we're already syncing or have attempted, skip
      if (syncAttemptedRef.current || isSyncing) {
        console.log('🔄 UserContext: Sync already in progress or attempted, skipping');
        return;
      }

      // Mark that we're syncing this token
      syncAttemptedRef.current = true;
      lastSyncedTokenRef.current = accessToken;
      setIsSyncing(true);

      console.log('🔄 UserContext: Starting bootstrap profile sync...');
      console.log('🔍 UserContext: Current userId in state:', userId);
      console.log('🔑 UserContext: Access token present:', !!accessToken);

      try {
        const result = await syncProfileFromToken(userId, accessToken, 'UserContext:bootstrap');

        if (result.success && result.profile) {
          console.log('✅ UserContext: Bootstrap sync successful');

          // If ID was corrected, update state and clear stale session
          if (result.correctedId) {
            console.log('🔧 UserContext: Correcting stale userId in state');
            setUserIdPersistent(result.profile.id);
            setFirstNamePersistent(result.profile.first_name);
            clearStaleSessionData(); // Clear old session ID
            console.log('🧹 UserContext: Cleared stale session data');
          }

          setIsProfileSynced(true);
          console.log('✅ UserContext: Profile sync complete, ready to lock ID');
        } else {
          // Sync failed - token invalid, expired, or DB error
          console.warn('⚠️ UserContext: Bootstrap sync failed:', result.error);

          // Handle specific error cases
          if (result.error === 'NO_TOKEN' || result.error === 'TOKEN_NOT_FOUND') {
            // Token is invalid/expired - clear local state
            console.log('🧹 UserContext: Clearing invalid token from localStorage');
            localStorage.removeItem('user_access_token');
            setUserIdPersistent(null);
            setFirstNamePersistent(null);
            clearStaleSessionData();
          }

          // Mark as synced (with failure) so we don't block the UI
          // Let the PostTestForm guard catch any remaining issues
          setIsProfileSynced(true);
          console.log('⚠️ UserContext: Marked as synced despite failure to prevent UI hang');
        }
      } catch (err) {
        console.error('❌ UserContext: Bootstrap sync error:', err);
        // Mark as synced on error to prevent UI hang
        setIsProfileSynced(true);
      } finally {
        setIsSyncing(false);
      }
    };

    // Set a 5-second timeout fallback
    const timeoutId = setTimeout(() => {
      if (!isProfileSynced && isSyncing) {
        console.warn('⏱️ UserContext: Bootstrap sync timeout - marking as synced to prevent UI hang');
        setIsProfileSynced(true);
        setIsSyncing(false);
      }
    }, 5000);

    performBootstrapSync();

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - intentionally empty deps to prevent re-sync

  // Trigger sync method for PreTestForm to call after profile creation
  const triggerProfileSync = async () => {
    if (typeof window === 'undefined') return;

    const accessToken = localStorage.getItem('user_access_token');
    if (!accessToken) {
      console.warn('⚠️ UserContext: triggerProfileSync called but no access_token found');
      return;
    }

    // Reset sync state to allow re-sync
    syncAttemptedRef.current = false;
    lastSyncedTokenRef.current = null;
    setIsProfileSynced(false);
    setIsSyncing(true);

    console.log('🔄 UserContext: Triggering manual profile sync after profile creation...');

    try {
      const result = await syncProfileFromToken(userId, accessToken, 'UserContext:manual-sync');

      if (result.success && result.profile) {
        console.log('✅ UserContext: Manual sync successful');
        setUserIdPersistent(result.profile.id);
        setFirstNamePersistent(result.profile.first_name);
        lastSyncedTokenRef.current = accessToken;
        setIsProfileSynced(true);
      } else {
        console.warn('⚠️ UserContext: Manual sync failed:', result.error);
        setIsProfileSynced(true); // Mark as synced to prevent blocking
      }
    } catch (err) {
      console.error('❌ UserContext: Manual sync error:', err);
      setIsProfileSynced(true);
    } finally {
      setIsSyncing(false);
    }
  };

  const setUserIdPersistent = (id: string | null) => {
    console.log('💾 UserContext: Setting persistent user ID:', id);
    setUserId(id);
    if (typeof window !== 'undefined') {
      if (id) {
        localStorage.setItem('cubetest-userId', id);
        console.log('💾 UserContext: User ID saved to localStorage:', id);
      } else {
        localStorage.removeItem('cubetest-userId');
        console.log('💾 UserContext: User ID removed from localStorage');
      }
    }
  };

  const setSessionIdPersistent = (id: string | null) => {
    setSessionId((prev) => (prev === id ? prev : id));
    if (typeof window !== 'undefined') {
      if (id) {
        localStorage.setItem('cubetest-sessionId', id);
      } else {
        localStorage.removeItem('cubetest-sessionId');
      }
    }
  };

  const setFirstNamePersistent = (name: string | null) => {
    setFirstName(name);
    if (typeof window !== 'undefined') {
      if (name) {
        localStorage.setItem('cubetest-firstName', name);
      } else {
        localStorage.removeItem('cubetest-firstName');
      }
    }
  };

  const clearUser = () => {
    setUserIdPersistent(null);
    setSessionIdPersistent(null);
    setFirstNamePersistent(null);
  };

  // Authentication methods
  const createAccount = async (email: string, password: string, firstName: string = 'User') => {
    console.log('🔐 UserContext: Starting account creation for:', email);
    
    // For direct sign-up from login page, create a new account
    const result = await AuthService.createAccountDirect(email, password, firstName);
    
    if (result.success && result.user && result.profile) {
      console.log('✅ UserContext: Account created successfully');
      console.log('📋 UserContext: Setting user ID to:', result.profile.id);
      console.log('👤 UserContext: Setting first name to:', result.profile.first_name);
      
      // Set the authentication state
      setAuthUser(result.user);
      setUserProfile(result.profile);
      setAuthLoading(false); // Important: set loading to false after successful signin
      
      // Also set the anonymous user state for compatibility
      setUserIdPersistent(result.profile.id);
      setFirstNamePersistent(result.profile.first_name);
      
      console.log('🎯 UserContext: User ID flow complete - ElevenLabs will receive:', result.profile.id);
    } else {
      console.error('❌ UserContext: Account creation failed:', result.error);
    }
    
    return result;
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔑 UserContext: Starting sign in for:', email);
    
    const result = await AuthService.signInWithEmail(email, password);
    
    if (result.success && result.user && result.profile) {
      console.log('✅ UserContext: Sign in successful');
      console.log('📋 UserContext: Setting user ID to:', result.profile.id);
      console.log('👤 UserContext: Setting first name to:', result.profile.first_name);
      
      // Set the authentication state
      setAuthUser(result.user);
      setUserProfile(result.profile);
      setAuthLoading(false); // Important: set loading to false after successful signin
      
      // Update the anonymous user state to match the authenticated profile
      setUserIdPersistent(result.profile.id);
      setFirstNamePersistent(result.profile.first_name);
      
      console.log('🎯 UserContext: User ID flow complete - ElevenLabs will receive:', result.profile.id);
    } else {
      console.error('❌ UserContext: Sign in failed:', result.error);
    }
    
    return result;
  };

  const signOut = async () => {
    // CRITICAL: Clear state IMMEDIATELY to prevent UI showing stale auth state
    console.log('🔄 UserContext: Clearing auth state immediately...');
    setAuthUser(null);
    setUserProfile(null);
    setAuthLoading(false);

    // Clear tokens from TokenManager
    TokenManager.clearTokens();
    console.log('✅ UserContext: Cleared TokenManager on sign out');

    // Clear profile access_token
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user_access_token');
      console.log('🗑️ UserContext: Removed user_access_token on sign out');
    }

    // Now call the sign out service
    const result = await AuthService.signOut();
    console.log('✅ UserContext: Sign out complete');

    return result;
  };

  const refreshProfile = async () => {
    if (authUser) {
      const profile = await AuthService.getUserProfile(authUser.id);
      setUserProfile(profile);
    }
  };

  return (
    <UserContext.Provider
      value={{
        // Anonymous user state
        userId,
        sessionId,
        firstName,
        setUserId: setUserIdPersistent,
        setSessionId: setSessionIdPersistent,
        setFirstName: setFirstNamePersistent,
        clearUser,

        // Profile sync state
        isProfileSynced,
        isSyncing,

        // Authentication state
        authUser,
        userProfile,
        isAuthenticated: !!authUser,
        authLoading,

        // Authentication methods
        createAccount,
        signIn,
        signOut,
        refreshProfile,
        triggerProfileSync,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

// Hook moved to separate file for better fast refresh support
export { UserContext };